import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
  CustomField,
  CustomFieldOption,
  CustomFieldValue,
  CustomFieldDependencyLevel,
} from "../hooks/useCompanyCustomFields";
import "./CustomFields.css";

interface CustomFieldRendererProps {
  field: CustomField;
  value: any;
  onChange: (fieldId: number, value: any) => void;
  onLoadOptions: (fieldId: number) => Promise<CustomFieldOption[]>;
  onLoadDependencyLevels?: (
    fieldId: number
  ) => Promise<CustomFieldDependencyLevel[]>;
  error?: string;
}

const CustomFieldRenderer: React.FC<CustomFieldRendererProps> = ({
  field,
  value,
  onChange,
  onLoadOptions,
  onLoadDependencyLevels,
  error,
}) => {
  const [options, setOptions] = useState<CustomFieldOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [dependencyLevels, setDependencyLevels] = useState<
    CustomFieldDependencyLevel[]
  >([]);
  const [loadingDependencyLevels, setLoadingDependencyLevels] = useState(false);
  const [validationError, setValidationError] = useState<string>("");

  const getFieldTypeIcon = (dataTypeKey: string): string => {
    const key = dataTypeKey?.toUpperCase();
    const iconMap: Record<string, string> = {
      TEXT: "ti ti-text-size",
      TEXTAREA: "ti ti-align-left",
      EMAIL: "ti ti-mail",
      URL: "ti ti-link",
      PHONE: "ti ti-phone",
      DATE: "ti ti-calendar",
      TIME: "ti ti-clock",
      DATETIME: "ti ti-calendar-time",
      DATE_RANGE: "ti ti-calendar-event",
      TIME_RANGE: "ti ti-clock-hour-4",
      DATE_TIME_RANGE: "ti ti-calendar-stats",
      INTEGER: "ti ti-123",
      INTEGER_VALUES: "ti ti-123",
      INTEGER_SMALL: "ti ti-123",
      INTEGER_LARGE: "ti ti-123",
      SMALL_INTEGER: "ti ti-123",
      SMALL_INTEGER_VALUES: "ti ti-123",
      SMALLINT: "ti ti-123",
      LARGE_INTEGER: "ti ti-123",
      LARGE_INTEGER_VALUES: "ti ti-123",
      BIGINT: "ti ti-123",
      DECIMAL: "ti ti-decimal",
      DECIMAL_VALUE: "ti ti-decimal",
      DECIMAL_VALUES: "ti ti-decimal",
      NUMBER: "ti ti-hash",
      FLOAT: "ti ti-decimal",
      FLOAT_VALUES: "ti ti-decimal",
      NUMERIC: "ti ti-hash",
      DOUBLE: "ti ti-decimal",
      DOUBLE_VALUES: "ti ti-decimal",
      DROPDOWN: "ti ti-chevron-down",
      RADIO_OPTION: "ti ti-circle-dot",
      MULTIPLE_OPTION: "ti ti-checkbox",
      SWITCH: "ti ti-toggle-right",
      BOOLEAN: "ti ti-toggle-right",
      FILE: "ti ti-file",
      IMAGE: "ti ti-photo",
      DEPENDENT_FIELD: "ti ti-hierarchy-3",
      CURRENCY: "ti ti-currency-dollar",
      PERCENTAGE: "ti ti-percentage",
      RATING: "ti ti-star",
      COLOR: "ti ti-color-swatch",
      JSON: "ti ti-code",
    };
    return iconMap[key] || "ti ti-forms";
  };

  const fieldType = field.custom_field_data_type.custom_field_data_type_key;
  const isChoiceField = [
    "DROPDOWN",
    "RADIO_OPTION",
    "MULTIPLE_OPTION",
  ].includes(fieldType);
  const isDependentField = fieldType === "DEPENDENT_FIELD";

  useEffect(() => {
    const loadFieldData = async () => {
      if (isDependentField) {
        setLoadingDependencyLevels(true);
        setLoadingOptions(true);
        try {
          const [fetchedLevels, fetchedOptions] = await Promise.all([
            onLoadDependencyLevels
              ? onLoadDependencyLevels(field.custom_field_id)
              : Promise.resolve([]),
            onLoadOptions(field.custom_field_id),
          ]);
          setDependencyLevels(fetchedLevels);
          setOptions(fetchedOptions);
        } catch (err) {
          console.error("Error loading dependent field data:", err);
        } finally {
          setLoadingDependencyLevels(false);
          setLoadingOptions(false);
        }
      } else if (isChoiceField && !field.options) {
        setLoadingOptions(true);
        try {
          const fetchedOptions = await onLoadOptions(field.custom_field_id);
          setOptions(fetchedOptions);
        } catch (err) {
          console.error("Error loading field options:", err);
        } finally {
          setLoadingOptions(false);
        }
      } else if (field.options) {
        setOptions(field.options);
      }
    };

    loadFieldData();
  }, [
    field.custom_field_id,
    isChoiceField,
    isDependentField,
    field.options,
    onLoadOptions,
    onLoadDependencyLevels,
  ]);


  const isEmptyValue = (val: any): boolean => {
    if (val === null || val === undefined || val === "") return true;
    if (typeof val === "object" && !Array.isArray(val) && Object.keys(val).length === 0) return true;
    if (Array.isArray(val) && val.length === 0) return true;
    return false;
  };

  const convertTo24HourFormat = (time12: string): string => {
    if (!time12 || typeof time12 !== "string") return "";

    const timeRegex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i;
    const match = time12.match(timeRegex);

    if (!match) return time12;

    let [, hours, minutes, seconds, ampm] = match;
    let hour = parseInt(hours, 10);

    if (ampm.toUpperCase() === "PM" && hour !== 12) {
      hour += 12;
    } else if (ampm.toUpperCase() === "AM" && hour === 12) {
      hour = 0;
    }

    return `${hour.toString().padStart(2, "0")}:${minutes}${
      seconds ? `:${seconds}` : ""
    }`;
  };

  const convertTo12HourFormat = (time24: string): string => {
    if (!time24) return "";

    const timeParts = time24.split(":");
    const hours = timeParts[0];
    const minutes = timeParts[1];
    const seconds = timeParts[2] || "00";

    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;

    return `${hour12}:${minutes}:${seconds} ${ampm}`;
  };

  const convertDateTimeFrom12HourFormat = (datetime12: string): string => {
    if (!datetime12 || typeof datetime12 !== "string") return "";

    const datetimeRegex =
      /^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i;
    const match = datetime12.match(datetimeRegex);

    if (!match) return datetime12;

    let [, year, month, day, hours, minutes, seconds, ampm] = match;
    let hour = parseInt(hours, 10);

    if (ampm.toUpperCase() === "PM" && hour !== 12) {
      hour += 12;
    } else if (ampm.toUpperCase() === "AM" && hour === 12) {
      hour = 0;
    }

    const secondsPart = seconds ? `:${seconds}` : ":00";
    return `${year}-${month}-${day}T${hour
      .toString()
      .padStart(2, "0")}:${minutes}${secondsPart}`;
  };

  const convertDateTimeTo12HourFormat = (datetime: string): string => {
    if (!datetime) return "";

    try {
      const date = new Date(datetime);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");

      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const seconds = date.getSeconds().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";

      hours = hours % 12 || 12;

      return `${year}-${month}-${day} ${hours
        .toString()
        .padStart(2, "0")}:${minutes}:${seconds} ${ampm}`;
    } catch {
      return datetime;
    }
  };

  const renderField = () => {
    const displayValue = !isEmptyValue(value) ? value : "";
    
    const commonProps = {
      id: `custom_field_${field.custom_field_id}`,
      className: `form-control ${error ? "is-invalid" : ""}`,
      placeholder:
        field.custom_field_description ||
        `Enter ${field.custom_field_display_name.toLowerCase()}`,
      required: field.is_required,
    };

    switch (fieldType) {
      case "TEXT":
      case "TEXT_SMALL":
      case "EMAIL":
      case "URL":
        return (
          <input
            {...commonProps}
            type={
              fieldType === "EMAIL"
                ? "email"
                : fieldType === "URL"
                ? "url"
                : "text"
            }
            maxLength={fieldType === "TEXT_SMALL" ? 255 : undefined}
            value={displayValue}
            onChange={(e) => onChange(field.custom_field_id, e.target.value)}
          />
        );

      case "TEXTAREA":
      case "TEXT_LARGE":
        return (
          <textarea
            {...commonProps}
            rows={3}
            maxLength={fieldType === "TEXT_LARGE" ? 2000 : undefined}
            value={displayValue}
            onChange={(e) => onChange(field.custom_field_id, e.target.value)}
          />
        );

      case "NUMBER":
      case "DECIMAL":
      case "DECIMAL_VALUE":
      case "DECIMAL_VALUES":
      case "FLOAT":
      case "FLOAT_VALUES":
      case "NUMERIC":
      case "DOUBLE":
      case "DOUBLE_VALUES":
        return (
          <input
            {...commonProps}
            type="number"
            step="0.01"
            value={!isEmptyValue(value) ? value : ""}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue === "") {
                onChange(field.custom_field_id, "");
              } else {
                const parsedValue = parseFloat(newValue);
                onChange(
                  field.custom_field_id,
                  isNaN(parsedValue) ? "" : parsedValue
                );
              }
            }}
          />
        );

      case "INTEGER":
      case "INTEGER_VALUES":
      case "INTEGER_SMALL":
      case "INTEGER_LARGE":
      case "SMALL_INTEGER":
      case "SMALL_INTEGER_VALUES":
      case "SMALLINT":
      case "LARGE_INTEGER":
      case "LARGE_INTEGER_VALUES":
      case "BIGINT":
        const isSmallInteger = ["INTEGER_SMALL", "SMALL_INTEGER", "SMALL_INTEGER_VALUES", "SMALLINT"].includes(fieldType);
        return (
          <input
            {...commonProps}
            type="number"
            step="1"
            value={!isEmptyValue(value) ? value : ""}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue === "") {
                setValidationError("");
                onChange(field.custom_field_id, "");
              } else {
                const parsedValue = parseInt(newValue, 10);
                if (isNaN(parsedValue)) {
                  setValidationError("");
                  onChange(field.custom_field_id, "");
                } else if (isSmallInteger && Math.abs(parsedValue) > 99999) {
                  setValidationError("Value cannot exceed 5 digits (maximum: 99999)");
                  return;
                } else {
                  setValidationError("");
                  onChange(field.custom_field_id, parsedValue);
                }
              }
            }}
          />
        );

      case "DATE":
        const dateValue = !isEmptyValue(value) ? value : "";
        return (
          <input
            {...commonProps}
            type="date"
            value={dateValue}
            onChange={(e) => onChange(field.custom_field_id, e.target.value || "")}
          />
        );

      case "TIME":
        const timeValue = !isEmptyValue(value) ? value : "";
        const displayTime = timeValue ? convertTo24HourFormat(timeValue) : "";
        return (
          <input
            {...commonProps}
            type="time"
            step="1"
            value={displayTime}
            onChange={(e) => {
              const time12 = e.target.value ? convertTo12HourFormat(e.target.value) : "";
              onChange(field.custom_field_id, time12);
            }}
          />
        );

      case "DATETIME":
        const datetimeValue = !isEmptyValue(value) ? value : "";
        const displayDateTime = datetimeValue
          ? convertDateTimeFrom12HourFormat(datetimeValue)
          : "";
        return (
          <input
            {...commonProps}
            type="datetime-local"
            step="1"
            value={displayDateTime}
            onChange={(e) => {
              const datetime12 = e.target.value ? convertDateTimeTo12HourFormat(e.target.value) : "";
              onChange(field.custom_field_id, datetime12);
            }}
          />
        );

      case "DATE_RANGE":
        const dateRangeRawValue = !isEmptyValue(value) ? value : null;
        const dateRangeValue =
          typeof dateRangeRawValue === "string" && dateRangeRawValue
            ? JSON.parse(dateRangeRawValue)
            : dateRangeRawValue || {};
        const startDate = dateRangeValue.start_date || "";
        const endDate = dateRangeValue.end_date || "";

        return (
          <div className="row g-2">
            <div className="col-6">
              <label className="form-label small">Start Date</label>
              <input
                type="date"
                className={`form-control ${error ? "is-invalid" : ""}`}
                value={startDate}
                onChange={(e) => {
                  const newValue = {
                    start_date: e.target.value,
                    end_date: endDate,
                  };
                  onChange(field.custom_field_id, JSON.stringify(newValue));
                }}
                required={field.is_required}
              />
            </div>
            <div className="col-6">
              <label className="form-label small">End Date</label>
              <input
                type="date"
                className={`form-control ${error ? "is-invalid" : ""}`}
                value={endDate}
                onChange={(e) => {
                  const newValue = {
                    start_date: startDate,
                    end_date: e.target.value,
                  };
                  onChange(field.custom_field_id, JSON.stringify(newValue));
                }}
                required={field.is_required}
              />
            </div>
          </div>
        );

      case "TIME_RANGE":
        const timeRangeRawValue = !isEmptyValue(value) ? value : null;
        const timeRangeValue =
          typeof timeRangeRawValue === "string" && timeRangeRawValue
            ? JSON.parse(timeRangeRawValue)
            : timeRangeRawValue || {};
        const startTime = timeRangeValue.start_time
          ? convertTo24HourFormat(timeRangeValue.start_time)
          : "";
        const endTime = timeRangeValue.end_time
          ? convertTo24HourFormat(timeRangeValue.end_time)
          : "";

        return (
          <div className="row g-2">
            <div className="col-6">
              <label className="form-label small">Start Time</label>
              <input
                type="time"
                step="1"
                className={`form-control ${error ? "is-invalid" : ""}`}
                value={startTime}
                onChange={(e) => {
                  const newValue = {
                    start_time: convertTo12HourFormat(e.target.value),
                    end_time: timeRangeValue.end_time || "",
                  };
                  onChange(field.custom_field_id, JSON.stringify(newValue));
                }}
                required={field.is_required}
              />
            </div>
            <div className="col-6">
              <label className="form-label small">End Time</label>
              <input
                type="time"
                step="1"
                className={`form-control ${error ? "is-invalid" : ""}`}
                value={endTime}
                onChange={(e) => {
                  const newValue = {
                    start_time: timeRangeValue.start_time || "",
                    end_time: convertTo12HourFormat(e.target.value),
                  };
                  onChange(field.custom_field_id, JSON.stringify(newValue));
                }}
                required={field.is_required}
              />
            </div>
          </div>
        );

      case "SWITCH":
        const switchRawValue = !isEmptyValue(value) ? value : false;
        const switchValue =
          switchRawValue === true || switchRawValue === "true" || switchRawValue === "TRUE" || switchRawValue === 1 || switchRawValue === '1';
        return (
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id={`custom_field_${field.custom_field_id}`}
              checked={switchValue}
              onChange={(e) =>
                onChange(field.custom_field_id, e.target.checked)
              }
            />
            <label
              className="form-check-label"
              htmlFor={`custom_field_${field.custom_field_id}`}
            >
              {switchValue ? "On" : "Off"}
            </label>
          </div>
        );

      case "BOOLEAN":
        const booleanRawValue = !isEmptyValue(value) ? value : false;
        const booleanValue = booleanRawValue === true || booleanRawValue === "true" || booleanRawValue === "TRUE" || booleanRawValue === 1 || booleanRawValue === '1';
        return (
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id={`custom_field_${field.custom_field_id}`}
              checked={booleanValue}
              onChange={(e) =>
                onChange(field.custom_field_id, e.target.checked)
              }
            />
            <label
              className="form-check-label"
              htmlFor={`custom_field_${field.custom_field_id}`}
            >
              {field.custom_field_description || "Enable this option"}
            </label>
          </div>
        );

       case "DROPDOWN":
         const selectOptions = options.map((option) => ({
           value: option.custom_field_choice_option_id,
           label: option.choice_label,
         }));
         
         const dropdownValue = !isEmptyValue(value) ? value : null;
         const selectedDropdownOption = dropdownValue 
           ? selectOptions.find((opt) => opt.value === dropdownValue || opt.label === dropdownValue) 
           : null;

         return (
           <Select
             options={selectOptions}
             value={selectedDropdownOption}
             onChange={(option) =>
               onChange(field.custom_field_id, option?.value || null)
             }
             placeholder={`Select ${field.custom_field_display_name.toLowerCase()}`}
             isClearable
             isLoading={loadingOptions}
             classNamePrefix="react-select"
             className={`custom-field-select ${error ? "is-invalid" : ""}`}
             menuPortalTarget={document.body}
             styles={{
               menuPortal: (base) => ({ ...base, zIndex: 9999 }),
             }}
           />
         );

      case "MULTIPLE_OPTION":
        const multiSelectOptions = options.map((option) => ({
          value: option.custom_field_choice_option_id,
          label: option.choice_label,
        }));

        const multiValue = !isEmptyValue(value) ? value : null;
        const selectedValues = Array.isArray(multiValue)
          ? multiValue
          : multiValue
          ? [multiValue]
          : [];
        const selectedOptions = multiSelectOptions.filter((opt) =>
          selectedValues.includes(opt.value) || selectedValues.includes(opt.label)
        );

         return (
           <Select
             options={multiSelectOptions}
             value={selectedOptions}
             onChange={(selectedOptions) => {
               const values = selectedOptions
                 ? selectedOptions.map((opt) => opt.value)
                 : [];
               onChange(field.custom_field_id, values);
             }}
             placeholder={`Select ${field.custom_field_display_name.toLowerCase()}`}
             isMulti
             isClearable
             isLoading={loadingOptions}
             classNamePrefix="react-select"
             className={`custom-field-select ${error ? "is-invalid" : ""}`}
             menuPortalTarget={document.body}
             styles={{
               menuPortal: (base) => ({ ...base, zIndex: 9999 }),
             }}
           />
         );

      case "RADIO_OPTION":
        const radioValue = !isEmptyValue(value) ? value : null;
        return (
          <div className="custom-field-radio-group">
            {options.map((option) => {
              const isChecked = radioValue === option.custom_field_choice_option_id || 
                                radioValue === option.choice_label;
              return (
                <div
                  key={option.custom_field_choice_option_id}
                  className="form-check"
                >
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`custom_field_${field.custom_field_id}`}
                    id={`custom_field_${field.custom_field_id}_${option.custom_field_choice_option_id}`}
                    value={option.custom_field_choice_option_id}
                    checked={isChecked}
                    onChange={(e) =>
                      onChange(field.custom_field_id, parseInt(e.target.value))
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`custom_field_${field.custom_field_id}_${option.custom_field_choice_option_id}`}
                  >
                    {option.choice_label}
                  </label>
                </div>
              );
            })}
          </div>
        );

      case "DEPENDENT_FIELD":
        if (loadingDependencyLevels || loadingOptions) {
          return (
            <div className="text-center py-3">
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <small className="text-muted">
                Loading dependent field data...
              </small>
            </div>
          );
        }

        if (dependencyLevels.length === 0) {
          return (
            <div className="alert alert-warning py-2 mb-0">
              <small>
                {!onLoadDependencyLevels
                  ? "Dependency levels loader not configured. Please ensure onLoadDependencyLevels is passed to the component."
                  : "No dependency levels configured for this field. Please configure dependency levels in settings."}
              </small>
            </div>
          );
        }

        let dependentValue = !isEmptyValue(value) && typeof value === "object" ? value : {};

        return (
          <div className="d-flex flex-column gap-2">
            {dependencyLevels
              .sort((a, b) => a.display_order - b.display_order)
              .map((level, levelIndex) => {
                let levelOptions = options.filter(
                  (opt) =>
                    opt.custom_field_dependency_level_id ===
                    level.custom_field_dependency_level_id
                );

                if (levelIndex > 0) {
                  const previousLevel = dependencyLevels.find(
                    (l) => l.display_order === level.display_order - 1
                  );
                  const previousSelectedOptionId = previousLevel
                    ? dependentValue[
                        previousLevel.custom_field_dependency_level_id
                      ]
                    : null;

                  if (previousSelectedOptionId) {
                    levelOptions = levelOptions.filter(
                      (opt) =>
                        opt.parent_choice_option_id === previousSelectedOptionId
                    );
                  } else {
                    levelOptions = [];
                  }
                }

                const levelValue =
                  dependentValue[level.custom_field_dependency_level_id];
                const selectOptions = levelOptions.map((opt) => ({
                  value: opt.custom_field_choice_option_id,
                  label: opt.choice_label,
                }));

                return (
                  <div key={level.custom_field_dependency_level_id}>
                    <label className="form-label small mb-1">
                      {level.dependency_level}
                      {field.is_required && levelIndex === 0 && (
                        <span className="custom-field-required">*</span>
                      )}
                    </label>
                    <Select
                      value={
                        levelValue
                          ? selectOptions.find(
                              (opt) => opt.value === levelValue
                            )
                          : null
                      }
                      onChange={(selected) => {
                        const newValue = { ...dependentValue };

                        if (selected?.value) {
                          newValue[level.custom_field_dependency_level_id] =
                            selected.value;
                        } else {
                          delete newValue[
                            level.custom_field_dependency_level_id
                          ];
                        }

                        dependencyLevels
                          .filter((l) => l.display_order > level.display_order)
                          .forEach((l) => {
                            delete newValue[l.custom_field_dependency_level_id];
                          });

                        onChange(
                          field.custom_field_id,
                          Object.keys(newValue).length > 0 ? newValue : null
                        );
                      }}
                      options={selectOptions}
                      placeholder={`Select ${level.dependency_level}`}
                      isClearable
                      isDisabled={
                        levelIndex > 0 &&
                        !dependentValue[
                          dependencyLevels.find(
                            (l) => l.display_order === level.display_order - 1
                          )?.custom_field_dependency_level_id || 0
                        ]
                      }
                      className={`custom-field-select ${
                        error ? "is-invalid" : ""
                      }`}
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                    {levelOptions.length === 0 && levelIndex > 0 && (
                      <small className="text-muted">
                        Select{" "}
                        {dependencyLevels[levelIndex - 1]?.dependency_level}{" "}
                        first
                      </small>
                    )}
                  </div>
                );
              })}
          </div>
        );

      default:
        return (
          <input
            {...commonProps}
            type="text"
            value={!isEmptyValue(value) ? value : ""}
            onChange={(e) => onChange(field.custom_field_id, e.target.value)}
          />
        );
    }
  };

  return (
    <div className="custom-field-group mb-3">
      <label className="form-label">
        <i 
          className={getFieldTypeIcon(field.custom_field_data_type.custom_field_data_type_key)} 
          style={{ 
            marginRight: "8px",
            color: "#6366f1",
            fontSize: "16px"
          }}
        />
        {field.custom_field_display_name}
        {field.is_required && <span className="custom-field-required">*</span>}
      </label>
      {loadingOptions && isChoiceField && !isDependentField ? (
        <div className="custom-fields-loading">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <small className="text-muted">Loading options...</small>
        </div>
      ) : (
        renderField()
      )}
      {error && <div className="invalid-feedback d-block">{error}</div>}
      {validationError && <div className="invalid-feedback d-block">{validationError}</div>}
    </div>
  );
};

export default CustomFieldRenderer;
